import BackspaceIcon from "@mui/icons-material/Backspace";
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { Box,Button,Dialog,DialogActions,DialogContent,DialogTitle,FormControl,FormControlLabel,IconButton,Radio,RadioGroup,Skeleton,Stack,Typography } from "@mui/material";
import { useMutation,useQuery } from "@tanstack/react-query";
import Signature,{ SignatureCanvasRef } from "@uiw/react-signature/canvas";
import { ChangeEvent,useContext,useEffect,useRef,useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserContext";
import AttToken from "../../helpers/attToken";
import Files from "../../service/Files";
import Users from "../../service/Users";
import { DownloadFilesResponse } from "../../types/user-card/types";
import { uploadFilesV2 } from "../helpers/checklists";


const uploadService = new Files();
const userService = new Users();

export default function ModalAssinatura({ open, close }: { open: boolean; close: Function }) {
  const { t } = useTranslation("translation");
  const { userData, saveUserData } = useContext(UserContext);
  const refAssinatura = useRef<SignatureCanvasRef>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const divRef = useRef<HTMLDivElement | null>(null);
  const [assinatura, setAssinatura] = useState<string>("");
  const [formato, setFormato] = useState("");
  const [fonteSelecionada, setFonteSelecionada] = useState("");
  const [userName, setUserName] = useState(userData.userName);
  const style = {
    width: 400,
    bgcolor: "inherit",
    border: "2px solid #D9D9DF",
    boxShadow: 24,
  };

  function handleClear() {
    if (refAssinatura.current) {
      refAssinatura.current.clear();
    }
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
    setAssinatura("");
  }

  function handleChangeFonte(fonte: string) {
    setFonteSelecionada(fonte);
  }

  const handleChangeFormato = (event: ChangeEvent<HTMLInputElement>) => {
    const valor = event.target.value;
    const palavras = userData.userName.trim().split(/\s+/);

    if (valor === "abreviado") {
      const primeiroNome = palavras[0];
      const abreviacoes = palavras.slice(1).map((palavra) => palavra[0].toUpperCase() + ".");
      setUserName(`${primeiroNome} ${abreviacoes.join(" ")}`);
    } else if (valor === "iniciais") {
      setUserName(palavras.map((palavra) => palavra[0].toUpperCase() + ".").join(" "));
    } else {
      setUserName(userData.userName);
    }

    setFormato(valor);
  };

  function handleSave() {
    const canvas = refAssinatura.current?.canvas || canvasRef.current;
    if (canvas) {
      setAssinatura(canvas.toDataURL());
    }
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && (fonteSelecionada || formato)) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.font = `30px ${fonteSelecionada}`;
        ctx.fillStyle = "black";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(userName, canvas.width / 2, canvas.height / 2);
        return setAssinatura(ctx.canvas.toDataURL());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userName, fonteSelecionada]);

  function base64ToBlob(base64DataUrl: string): Blob {
    const parts = base64DataUrl.split(",");
    const mimeType = parts[0].match(/:(.*?);/)?.[1] || "image/webp";
    const byteString = atob(parts[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }
    return new Blob([uint8Array], { type: mimeType });
  }

  const { mutate, isPending, isError } = useMutation({
    mutationKey: ["SALVAR_ASSINATURA"],
    mutationFn: async () => {
      toast.info(t('custom.salvando_modelo_de_assinatura'), { toastId: 'custom.salvando_modelo_de_assinatura' });
      try {
        const promise = toast.promise(async () => {
          const token = await AttToken();
          if (!token) throw new Error('Token não encontrado');
          if (token) {
            const blob = base64ToBlob(assinatura);
            const file = new File([blob], "assinatura.webp", { type: "image/webp" });
            const saveImg: DownloadFilesResponse | undefined = await uploadFilesV2([file], `profile/${token.username}`);
            if (!saveImg) throw new Error('Erro ao salvar assinatura');
            if (saveImg) {
              const editUser = await userService.changeUser(token, token.username, {
                contato: userData.phone,
                email: userData.email,
                empresa: token.userEmpresa,
                id: token.username,
                matricula: userData.matricula,
                nome: userData.userName,
                perfil: userData.role,
                assinatura: `profile/${token.username}/assinatura.webp`,
                aprovador: true,
              });
              if (editUser.message === `Usuário ${userData.userName} alterado com sucesso!`) {
                return editUser;
              }
            }
          }
        }, {
          pending: t('custom.salvando_modelo_de_assinatura'),
          success: t('custom.assinatura_salva'),
          error: t('custom.erro_ao_salvar_assinatura')
        }, { toastId: 'custom.salvando_modelo_de_assinatura' });
        return promise;
      } catch (err) {
        throw new Error(t('custom.erro_ao_salvar_assinatura'));
      }
    },
    onSuccess: () => {
      saveUserData({ ...userData, assinatura: `profile/${userData.id}/assinatura.webp` });
      close();
    }
  });

  const { isLoading } = useQuery({
    queryKey: ["GET_ASSINATURA"],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token não encontrado');
        if (token) {
          const response = await uploadService.downloadFiles(token, "web", "-", [userData.assinatura]);
          if (!response) throw new Error('Erro ao buscar assinatura');
          if (response) {
            for (const image of response.urls) {
              const blob = await fetch(image).then((res) => res.blob());
              const file = URL.createObjectURL(blob);
              if (file) {
                return setAssinatura([file][0]);
              }
            }
          }
        }
      } catch (err) {
        throw new Error(t('custom.erro_ao_buscar_assinatura'));
      }
    },
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });

  const onLoading = isLoading || isPending;

  if (isError) {
    toast.error(t('custom.erro_ao_salvar_assinatura'), { toastId: 'custom.erro_ao_salvar_assinatura' });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={() => close()}
      scroll={'paper'}
    >
      <DialogTitle>Configurar assinatura</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => close()}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        <Stack direction="column" spacing={1.5} alignItems="center" justifyContent="center">
          <Typography textAlign="center" color="textSecondary" variant="subtitle2" fontSize={13} fontWeight={600}>
            Crie sua assinatura ou escolha um modelo predefinido
          </Typography>
          <FormControl>
            <RadioGroup defaultValue={formato} row aria-labelledby="demo-row-radio-buttons-group-label" name="row-radio-buttons-group">
              <FormControlLabel value="completo" control={<Radio onChange={handleChangeFormato} />} label="Nome completo" />
              <FormControlLabel value="abreviado" control={<Radio onChange={handleChangeFormato} />} label="Nome abreviado" />
              <FormControlLabel value="iniciais" control={<Radio onChange={handleChangeFormato} />} label="Iniciais" />
            </RadioGroup>
          </FormControl>
          <Stack direction="row" width="100%" justifyContent="space-between" alignItems="centeri">
            <button
              onClick={() => handleChangeFonte("Cedarville Cursive")}
              className={`button ${fonteSelecionada === "Cedarville Cursive" ? "is-info" : ""}`}
              style={{ fontFamily: "Cedarville Cursive", fontWeight: 600 }}>
              {userName}
            </button>
            <button
              onClick={() => handleChangeFonte("Playwrite DE SAS")}
              className={`button ${fonteSelecionada === "Playwrite DE SAS" ? "is-info" : ""}`}
              style={{ fontFamily: "Playwrite DE SAS", fontWeight: 600 }}>
              {userName}
            </button>
            <button
              onClick={() => handleChangeFonte("Caveat")}
              className={`button ${fonteSelecionada === "Caveat" ? "is-info" : ""}`}
              style={{ fontFamily: "Caveat", fontWeight: 600 }}>
              {userName}
            </button>
          </Stack>
          {isLoading ? (
            <Skeleton variant="rounded" height={150} width={300} />
          ) : (
            <Box sx={style}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0}>
                <>
                  <Signature
                    style={{ display: !assinatura ? "" : "none", backgroundColor: "transparent" }}
                    width={300}
                    height={150}
                    ref={refAssinatura}
                  />
                  <canvas ref={canvasRef} width={300} height={150} style={{ display: "none", backgroundColor: "transparent" }} />
                  <div ref={divRef}>
                    <img
                      alt=""
                      style={{ display: assinatura ? "" : "none", backgroundColor: "transparent" }}
                      width={300}
                      height={150}
                      src={assinatura}
                    />
                  </div>
                </>
                <Stack
                  style={{ width: "fit-content", height: "100%" }}
                  direction="column"
                  justifyContent="flex-end"
                  alignItems="flex-end"
                  spacing={4}>
                  {!assinatura ? (
                    <Stack direction="column" alignItems="flex-end" justifyContent="center" sx={{ paddingRight: "10px" }}>
                      <IconButton onClick={() => handleClear()} color="info" size="large">
                        <BackspaceIcon fontSize="large" />
                      </IconButton>
                      <IconButton onClick={() => handleSave()} color="success" size="large">
                        <SaveIcon fontSize="large" />
                      </IconButton>
                    </Stack>
                  ) : (
                    <IconButton onClick={() => handleClear()} color="error" size="large">
                      <DeleteIcon fontSize="large" />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={onLoading} onClick={() => mutate()}>
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
